import { useEffect } from 'react'
import store from 'store'
import { useSelector, useDispatch } from 'react-redux'
import { selectAccessToken } from '../../../routing/fetchingfunction/user/user.reducer'
import { setRefreshedTokens } from '../../../routing/fetchingfunction/user/user.action'

/**
 * Enhanced utility hook to ensure token synchronization during registration
 * This fixes issues with 401 errors when submitting the About form
 */
const useRegistrationTokenSync = () => {
  const dispatch = useDispatch()
  const reduxAccessToken = useSelector(selectAccessToken)

  useEffect(() => {
    // Comprehensive function to ensure tokens are synchronized across all storage mechanisms
    const synchronizeTokens = () => {
      try {
        console.log('Synchronizing tokens for registration form submission...')

        // Get refresh token from localStorage or store.js since it's not in Redux selectors
        const localStorageRefreshToken = localStorage.getItem('refresh_token')
        const storeJsRefreshToken = store.get('refresh_token')

        // Sources priority: Redux > localStorage > store.js
        const bestAccessToken =
          reduxAccessToken || localStorage.getItem('access_token') || store.get('access_token')

        const bestRefreshToken = localStorageRefreshToken || storeJsRefreshToken

        if (!bestAccessToken || !bestRefreshToken) {
          console.warn('Cannot synchronize tokens - one or both tokens missing')
          return
        }

        // 1. Update localStorage
        localStorage.setItem('access_token', bestAccessToken)
        localStorage.setItem('refresh_token', bestRefreshToken)

        // 2. Update store.js
        store.set('access_token', bestAccessToken)
        store.set('refresh_token', bestRefreshToken)

        // 3. Update Redux if needed via dispatch
        if (bestAccessToken !== reduxAccessToken) {
          dispatch(
            setRefreshedTokens({
              access: bestAccessToken,
              refresh: bestRefreshToken,
            })
          )
        }

        // 4. Set tempAuthToken for immediate use
        window.tempAuthToken = bestAccessToken

        console.log('Token synchronization completed successfully')
      } catch (e) {
        console.error('Error during token synchronization:', e)
      }
    }

    // Run once on component mount
    synchronizeTokens()

    // Clean up on unmount
    return () => {
      window.tempAuthToken = null
    }
  }, [dispatch, reduxAccessToken])

  // Return true if we have valid access token and refresh token in localStorage
  return !!reduxAccessToken && !!localStorage.getItem('refresh_token')
}

export default useRegistrationTokenSync
